import { useCallback } from "react";
import { SSH_User } from "@danishagro/sharedShop/src/interfaces/user.interface";
import { SSH_UserRoles } from "@danishagro/sharedShop/src/interfaces/userRoles.interface";
import { SSH_InviteAccount } from "@danishagro/sharedShop/src/interfaces/inviteAccount.interface";
import { FetchOptions } from "@danishagro/shared/src/interfaces/fetchOptions.interface";
import { useRequest } from "@danishagro/shared/src/hooks/useRequest.hook";

type GetUsers = (customerNumber: string, options?: FetchOptions) => Promise<SSH_User[]>;

type GetUser = (
    userId: string,
    customerNumber: string,
    options?: FetchOptions
) => Promise<SSH_UserRoles>;

type EditUser = (
    userId: string,
    parameters: {
        roleAlias: string;
        customerNumbers: string[];
        active: boolean;
    },
    options?: FetchOptions
) => Promise<unknown>;

type EditUserV2 = (
    userId: string,
    parameters: {
        roleAlias: string;
        customerNumbers: {
            customerNumber: string;
            active: boolean;
        }[];
    },
    options?: FetchOptions
) => Promise<unknown>;

type DeleteUser = (userId: string, cvr: string, options?: FetchOptions) => Promise<unknown>;

type GetInviteAccounts = (customerNumber: string) => Promise<SSH_InviteAccount[]>;

type GetUsersRaw = (
    customerNumber: string,
    options?: FetchOptions
) => Promise<{ allowInvite: boolean; users: SSH_User[] }>;

export const useUserRolesApi = () => {
    const request = useRequest();

    const getUsers: GetUsers = useCallback(
        async (customerNumber, options?) => {
            const res = await request.get(
                `api/user/relatedcustomernumbers/${customerNumber}`,
                null,
                options
            );
            return Array.isArray(res) ? res : res && Array.isArray(res.users) ? res.users : [];
        },
        [request]
    );

    const getUser: GetUser = useCallback(
        (userId, customerNumber, options?) =>
            request.get(`api/userroles/${userId}/customernumber/${customerNumber}`, null, options),
        [request]
    );

    const getDeletedUsers: GetUsers = useCallback(
        async (customerNumber, options?) => {
            const res = await request.get(`api/user/deletedusers/${customerNumber}`, null, options);
            return Array.isArray(res) ? res : res && Array.isArray(res.users) ? res.users : [];
        },
        [request]
    );

    const editUser: EditUser = useCallback(
        (userId, parameters, options?) =>
            request.put(`api/userroles/${userId}`, parameters, options),
        [request]
    );

    const editUserV2: EditUserV2 = useCallback(
        (userId, parameters, options?) =>
            request.put(`api/userroles/${userId}`, parameters, options),
        [request]
    );

    const deleteUser: DeleteUser = useCallback(
        (userId, customerNumber, options?) =>
            request.delete(
                `api/userroles/${userId}/customernumber/${customerNumber}`,
                null,
                options
            ),
        [request]
    );

    const getInviteAccounts: GetInviteAccounts = useCallback(
        (customerNumber, options?) =>
            request.get(`api/user/inviteaccounts/customernumber/${customerNumber}`, null, options),
        [request]
    );

    // In your hook:
    const getUsersRaw: GetUsersRaw = useCallback(
        (customerNumber, options?) =>
            request.get(`api/user/relatedcustomernumbers/${customerNumber}`, null, options),
        [request]
    );

    return {
        getUsers,
        getUsersRaw,
        getUser,
        editUser,
        editUserV2,
        deleteUser,
        getInviteAccounts,
        getDeletedUsers,
    };
};
